/**
 * <uterific-carousel> — a scroll-snap image carousel with arrows and dots.
 *
 * The track is a plain horizontally scrolling list of slides, so swiping works with no
 * script at all; this element only drives the arrow buttons and keeps the dots in step
 * with whichever slide is in view. Nothing auto-advances.
 *
 * Children it looks for:
 *   [data-ut-track]      the scrolling list
 *   [data-ut-slide]      each slide inside it
 *   [data-ut-prev/next]  the arrow buttons
 *   [data-ut-dot]        one per slide, in order
 *
 * Load this with type="module".
 */

class UterificCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ut-track]');
    this.slides = Array.from(this.querySelectorAll('[data-ut-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-ut-dot]'));
    if (!this.track || this.slides.length < 2) {
      this.classList.add('is-single');
      return;
    }

    this.index = 0;
    this.querySelector('[data-ut-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-ut-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    this.frame = 0;
    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.sync());
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  go(i) {
    const n = this.slides.length;
    const next = ((i % n) + n) % n;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: reduce ? 'instant' : 'smooth' });
  }

  sync() {
    this.frame = 0;
    const width = this.track.clientWidth || 1;
    this.index = Math.round(this.track.scrollLeft / width);
    this.dots.forEach((dot, i) => {
      const on = i === this.index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }
}

if (!customElements.get('uterific-carousel')) {
  customElements.define('uterific-carousel', UterificCarousel);
}
